/**
 * Form for editing patient summary with AI generation support.
 * @agentic connections
 */

// Components
import {
  Stack,
  Button,
  Group,
  Textarea,
  Text,
  Loader,
  Alert,
} from "@mantine/core";
import { IconSparkles, IconAlertCircle } from "@tabler/icons-react";

// Hooks
import { useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";
import { notifications } from "@mantine/notifications";
import { showErrorNotification } from "@benchmark/ui/utils";

// Types
import type { Patient } from "@medplum/fhirtypes";
import type { PatientSummary } from "@benchmark/core/types";

// Utils
import {
  getPatientSummary,
  updatePatientSummaryExtension,
  removePatientSummaryExtension,
} from "@benchmark/core/resolvers";
import { globalEventEmitter, DomainEventNames } from "@benchmark/core/events";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";
import { useGeneratePatientSummary } from "@/hooks/api/useGeneratePatientSummary";

/**
 * Props for the patient summary form.
 * @agentic connections
 */
export type PatientSummaryFormProps = {
  patient: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/**
 * AI model and prompt version metadata for generated summaries.
 * @agentic connections
 */
type AIMetadata = {
  model: string;
  promptVersion: string;
};

/**
 * Form for editing patient summary with optional AI generation.
 * @agentic connections
 */
export const PatientSummaryForm = ({
  patient,
  onSuccess,
  onCancel,
}: PatientSummaryFormProps) => {
  const medplum = useMedplum();
  const { practitionerRole, isLoading: isLoadingRole } =
    useCurrentPractitionerRole();
  const {
    generateSummary,
    isGenerating,
    error: generateError,
  } = useGeneratePatientSummary();

  const existingSummary = getPatientSummary(patient);
  const [summary, setSummary] = useState(existingSummary?.text ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track AI metadata for preservation on save
  const [aiMetadata, setAiMetadata] = useState<AIMetadata | null>(
    // If existing summary was AI-generated, preserve its metadata
    existingSummary?.generatedBy === "ai" && existingSummary.modelUsed
      ? {
          model: existingSummary.modelUsed,
          promptVersion: existingSummary.promptVersion ?? "",
        }
      : null,
  );

  const handleSave = useCallback(async () => {
    if (!practitionerRole?.id) {
      showErrorNotification("Unable to determine current user");
      return;
    }

    setIsSubmitting(true);

    try {
      const authorReference = {
        reference: `PractitionerRole/${practitionerRole.id}`,
        display: practitionerRole.practitioner?.display ?? "Unknown",
      };

      let updatedExtensions: Patient["extension"];

      if (summary.trim()) {
        // Determine if this is AI-generated content
        const generatedBy: PatientSummary["generatedBy"] = aiMetadata
          ? "ai"
          : "manual";

        updatedExtensions = updatePatientSummaryExtension(patient.extension, {
          text: summary.trim(),
          authorReference,
          generatedBy,
          modelUsed: aiMetadata?.model,
          promptVersion: aiMetadata?.promptVersion,
        });
      } else {
        updatedExtensions = removePatientSummaryExtension(patient.extension);
      }

      const updatedPatient = await medplum.updateResource({
        ...patient,
        extension: updatedExtensions,
      });

      // Publish so the profile card / patient detail refresh the summary
      // without a prop-drilled refetch. (Tracked: this direct write should
      // move into a core action that owns the emission — see issue.)
      if (updatedPatient.id) {
        globalEventEmitter.publish(DomainEventNames.PatientUpdated, {
          patientId: updatedPatient.id,
          patient: updatedPatient,
        });
      }

      notifications.show({
        title: "Success",
        message: "Patient summary updated",
        color: "green",
      });

      onSuccess?.();
    } catch {
      showErrorNotification("Failed to update patient summary");
    } finally {
      setIsSubmitting(false);
    }
  }, [summary, patient, practitionerRole, medplum, aiMetadata, onSuccess]);

  const performGeneration = useCallback(async () => {
    if (!patient.id) return;

    const result = await generateSummary(patient.id);
    if (result) {
      setSummary(result.summary);
      setAiMetadata({
        model: result.metadata.model,
        promptVersion: result.metadata.promptVersion,
      });
      notifications.show({
        title: "Summary generated",
        message: `Confidence: ${result.confidence}. Review and edit as needed, then save.`,
        color: "blue",
      });
    }
  }, [patient.id, generateSummary]);

  const handleGenerate = useCallback(async () => {
    // If there's existing content, show confirmation
    if (summary.trim()) {
      const confirmed = window.confirm(
        "Replace existing summary?\n\nThis will replace your current summary with an AI-generated one. You can edit the result before saving.",
      );
      if (!confirmed) return;
    }
    await performGeneration();
  }, [summary, performGeneration]);

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSummary(e.currentTarget.value);
      // Clear AI metadata if user substantially edits the content
      // (Keep metadata if just minor edits to preserve AI attribution)
      // For now, we keep metadata - user can always regenerate if they want fresh AI content
    },
    [],
  );

  // Info about existing summary
  const existingInfo = existingSummary
    ? `Last updated by ${existingSummary.authorDisplay}${
        existingSummary.generatedBy === "ai" ? " (AI generated)" : ""
      }`
    : null;

  return (
    <Stack gap="md">
      {existingInfo && (
        <Text size="sm" c="dimmed">
          {existingInfo}
        </Text>
      )}

      {generateError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {generateError.message}
        </Alert>
      )}

      {aiMetadata && !existingSummary?.modelUsed && (
        <Alert color="blue" icon={<IconSparkles size={16} />}>
          AI-generated summary ready for review. Edit as needed before saving.
        </Alert>
      )}

      <Textarea
        label="Summary"
        description="Brief overview for clinicians (2 sentences recommended)"
        placeholder="e.g., 45yo female with history of depression seeking mindfulness-based therapy; afternoon availability, virtual preferred."
        value={summary}
        onChange={handleTextChange}
        minRows={3}
        maxRows={6}
        autosize
        disabled={isGenerating}
      />

      <Group justify="space-between">
        <Button
          variant="light"
          color="blue"
          leftSection={
            isGenerating ? <Loader size={14} /> : <IconSparkles size={14} />
          }
          onClick={handleGenerate}
          disabled={isGenerating || isSubmitting || !patient.id}
        >
          {isGenerating ? "Generating..." : "Generate with AI"}
        </Button>

        <Group gap="sm">
          <Button variant="subtle" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSubmitting}
            disabled={isLoadingRole || isGenerating}
          >
            Save
          </Button>
        </Group>
      </Group>
    </Stack>
  );
};
